import { Menu, Transition } from '@headlessui/react';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { Fragment } from 'react';

export interface KpiDashboardExportMenuProps {
  disabled?: boolean;
  /** When true, trigger is disabled and shows a short preparing label (async exports). */
  loading?: boolean;
  onExportPdf?: () => void;
  onExportExcel: () => void;
  /** When false, PDF option is hidden. */
  showPdf?: boolean;
  /** When false, only PDF is listed (Excel is for the main Dashboard tab summaries). */
  showExcel?: boolean;
  /** Optional CSV row (e.g. KPI drill-down). */
  showCsv?: boolean;
  onExportCsv?: () => void;
  ariaLabel: string;
  /** Visible label on the trigger (default "Export"). Use in backlog modal: "Export summary" / "Export detail summary". */
  triggerLabel?: string;
  /** Optional Tailwind classes for the trigger button (e.g. emerald styling for detail export). */
  buttonClassName?: string;
}

const DEFAULT_BUTTON_CLASS =
  'inline-flex items-center gap-1 rounded-md border border-indigo-100 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:border-indigo-200 hover:bg-indigo-50/50 hover:text-indigo-950 disabled:cursor-not-allowed disabled:opacity-50';

const KpiDashboardExportMenu: React.FC<KpiDashboardExportMenuProps> = ({
  disabled = false,
  loading = false,
  onExportPdf = () => {},
  onExportExcel,
  showPdf = true,
  showExcel = true,
  showCsv = false,
  onExportCsv,
  ariaLabel,
  triggerLabel = 'Export',
  buttonClassName,
}) => (
  <Menu as="div" className="relative inline-block text-left">
    <Menu.Button
      type="button"
      disabled={disabled || loading}
      className={buttonClassName ?? DEFAULT_BUTTON_CLASS}
      aria-label={ariaLabel}
    >
      {loading ? (
        <span className="inline-flex items-center gap-1.5 text-slate-600">
          <span
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600"
            aria-hidden
          />
          Preparing…
        </span>
      ) : (
        <>
          <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {triggerLabel}
          <ChevronDownIcon className="h-3 w-3 shrink-0 text-slate-500" aria-hidden />
        </>
      )}
    </Menu.Button>
    <Transition
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <Menu.Items className="absolute right-0 z-[70] mt-1 w-40 origin-top-right rounded-md border border-slate-200 bg-white py-1 shadow-lg ring-1 ring-slate-950/5 focus:outline-none">
        {showPdf ? (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                className={`${active ? 'bg-slate-50 text-slate-900' : 'text-slate-700'} block w-full px-3 py-2 text-left text-xs`}
                onClick={onExportPdf}
              >
                PDF
              </button>
            )}
          </Menu.Item>
        ) : null}
        {showExcel ? (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                className={`${active ? 'bg-slate-50 text-slate-900' : 'text-slate-700'} block w-full px-3 py-2 text-left text-xs`}
                onClick={onExportExcel}
              >
                Excel
              </button>
            )}
          </Menu.Item>
        ) : null}
        {showCsv ? (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                className={`${active ? 'bg-slate-50 text-slate-900' : 'text-slate-700'} block w-full px-3 py-2 text-left text-xs`}
                onClick={() => onExportCsv?.()}
              >
                CSV
              </button>
            )}
          </Menu.Item>
        ) : null}
      </Menu.Items>
    </Transition>
  </Menu>
);

export default KpiDashboardExportMenu;
